import { initWebAuthGateIfNeeded } from "@/client/core/services/web-auth.service";
import { applyDisplayMode, getDisplayMode } from "@/client/utils/getDisplayMode";
import { installSidepanelHostMarker } from "@/client/utils/sidepanel-host-marker";
import { initFeatureFlags } from "@/client/core/services/feature-flags.signal";
import { createSidepanelAuthBridge } from "@/client/core/services/sidepanel-auth-bridge";
import { trackEvent } from "@/client/utils/telemetry";
import { createEl } from "@/client/utils/createEl";
import { onDOMReady } from "@/client/utils/onDOMReady";
import { createAppDirectory } from "@/client/ui/components/app-directory.component";
import { createAppMenu } from "@/client/ui/components/app-menu/app-menu.component";
import { mountSoundsBrowser } from "@/client/ui/components/sounds-browser/sounds-browser.component";
import { createCore } from "@/client/core/Core";
import { kAllAudiences, type AppDefinition } from "@/client/core/Core.api";
import * as styles from "./ui/design-system/styles/global.css";

/**
 * The home page as a Core host. Not a deployed app: no page entry of its own,
 * no card, no audience gate. It exists so the Splice Sounds dock can audition
 * samples here the way it does on every app page, through the one preview
 * player, rather than home being the one page with a different one.
 */
const kHomeDefinition: AppDefinition = {
    title: "Backstage",
    description:
        "A first-look at the next horizon of AI-enabled creative workflows, powered by the latest research from Splice R&D.",
    directory: "home",
    audiences: kAllAudiences,
};

/**
 * Embedded hosts (the Labs plugin and the Sounds-plugin sidepanel) boot the
 * home page without a Core: the dock is a website feature, and a Core here
 * would open a plugin channel under a namespace the host does not know.
 */
const bootEmbeddedHome = (): void => {
    applyDisplayMode();
    // Before anything renders a link: in-app navigation must carry the host
    // marker forward, or the next page drops into plugin mode.
    installSidepanelHostMarker();
    if (window.parent !== window) {
        // The home page has no Core, so the MessageChannel handshake that
        // apps set up in createCore never happens here. Embedded hosts
        // deliver auth tokens over that channel — and audience resolution
        // needs the token — so listen for the handshake directly.
        createSidepanelAuthBridge();
    }
    initFeatureFlags();
    trackEvent("app_opened");
    initWebAuthGateIfNeeded((error) => {
        // The home page has no notification UI — log and leave the gate up.
        // eslint-disable-next-line no-console
        console.error("Splice login", error);
    });
};

onDOMReady(() => {
    const isWeb = getDisplayMode() === "web";
    if (isWeb) {
        // The website boots home like an app page: display mode, host marker,
        // feature flags, login gate and `app_opened` all happen in createCore.
        // No access guard (everyone can see home) and no MIDI (nothing here
        // listens, and the permission prompt would be the only effect).
        const core = createCore(kHomeDefinition, {
            shouldGuardAccess: false,
            shouldEnableMidi: false,
        });
        document.body.appendChild(createEl("div", styles.view, [createAppDirectory()]));
        document.body.appendChild(
            createAppMenu({
                title: kHomeDefinition.title,
                isHome: true,
                description: kHomeDefinition.description,
            }),
        );
        mountSoundsBrowser(core);
        return;
    }

    bootEmbeddedHome();
    document.body.appendChild(createEl("div", styles.view, [createAppDirectory()]));
    document.body.appendChild(
        createAppMenu({
            title: kHomeDefinition.title,
            isHome: true,
            description: kHomeDefinition.description,
        }),
    );
});
